import { createSelector } from 'reselect';
import _ from 'lodash';

import itemTypePluralize from '../../components/common/itemTypePluralrize';
import { CollectionFilterTypes, UnownedOwnershipStatuses } from '../../config/constants';

const collections = state => state.collections.items;
const catSplitAttrs = state => state.collections.catSplitAttributes;
const filterType = state => state.collections.filterType;
/**
 * Transforms the flat list of items into a double-pivoted list following this format:
 * {
 *   [CATEGORY]: {
 *     [Category-default specificData Attribute]: [all, the, matching, items]
 *     ...
 *   }
 *   ...
 * }
 * so it could look like this:
 * {
 *   WATCH: {
 *     Rolex: [a, b, f],
 *     'Audemar Piget': [c, d],
 *   },
 *   CAR: {
 *     Porsche: [e],
 *   },
 * }
 */
export const collectionsByType = createSelector(
  [collections, catSplitAttrs, filterType],
  (colls, splitAttrs, filter) => {
    const result = colls.reduce((prev, curr) => {
      // Step 1: Filter out any by filterType
      let shouldAdd = true;
      switch (filter) {
        case CollectionFilterTypes.ALL:
          shouldAdd = true;
          break;
        case CollectionFilterTypes.IN_COLL:
          shouldAdd = curr.getIsOwned();
          break;
        case CollectionFilterTypes.NOT_IN_COLL:
          shouldAdd = !curr.getIsOwned();
          break;
        // no default
      }

      if (!shouldAdd) {
        return prev;
      }

      // Step 2: Build the object and add the item
      let cat = curr.category;

      if (cat === 'OTHER' && curr.specificData.o_type) {
        cat = `${curr.specificData.o_type}`;
      }

      const splitAttrCat = (curr.category === 'OTHER' && splitAttrs[cat]) ? cat : curr.category;

      const defaultAttr = splitAttrs[splitAttrCat];
      const specific = curr.specificData[defaultAttr];

      if (!prev[cat]) {
        prev[cat] = {};
      }

      if (!prev[cat][specific]) {
        prev[cat][specific] = [];
      }

      prev[cat][specific].push(curr);

      return prev;
    }, {});

    return result;
  }
);

export const getComparativeData = createSelector(collections, (colls) => {
  return _.reduce(
    _.groupBy(colls, 'category'),
    (accum, value, key) => {
      const ownedItems = value.filter(v => UnownedOwnershipStatuses.indexOf(v.itemData.ownership_status) === -1);
      const avg = _.meanBy(ownedItems, (v) => {
        const val = parseFloat(v.itemData.market_value);
        return Number.isNaN(val) ? 0 : val;
      });
      accum[key] = avg;
      return accum;
    },
    {}
  );
});

export const getComparativeChartData = createSelector(getComparativeData, (data) => {
  return _.map(data, (value, key) => ({
    category: itemTypePluralize(key),
    [`${key.toLowerCase()}AvgMarketValue`]: value,
  }));
});



// WEBPACK FOOTER //
// ./src/redux/selectors/collections.js